import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Paper, Select, Skeleton, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

import useSmqPermissions from "../../../hooks/useSmqPermissions";
import UserService from "../../../services/UserService";
import OutilService, { OUTIL_ERROR_MESSAGES } from "../../../services/smq/OutilService";
import { fieldSx, OUTIL_COLORS, paperSx, tableHeaderSx } from "./OutilContainer.styles";
import { ToolSectionHeader } from "./OutilShared";

const SYMBOLS = [
  { color: "#173d61", label: "Début / Fin", value: "start_end" },
  { color: "#2176d2", label: "Étape / séquence", value: "step" },
  { color: "#0f4da0", label: "Test / Condition", value: "condition" },
  { color: "#1769bd", label: "Document / fichier", value: "document" },
  { color: "#154a8d", label: "BDD", value: "database" },
];
const TABLES = {
  activites: { title: "Activités / Éléments entrants et sortants", columns: [["entree", "Éléments entrants"], ["activite", "Activité principale"], ["sortie", "Éléments sortants"]] },
  indicateurs: { title: "Indicateurs et valeurs", columns: [["indicateur", "Indicateur"], ["valeur", "Valeur"]] },
  ressources: { title: "Ressources", columns: [["ressources_humaines", "Ressources humaines"], ["ressources_materielles", "Ressources matérielles"]] },
  autres: { title: "Autres", columns: [["documents", "Documents"], ["risques", "Risques"], ["opportunites", "Opportunités"], ["leader_smq_id", "Leader SMQ"]] },
};
const EMPTY = { processus: {}, activites: [], indicateurs: [], ressources: [], autres: [], logigramme: [] };
let keyIndex = 0;
const nextKey = () => "outil3-" + Date.now() + "-" + (++keyIndex);
const sameId = (a, b) => a != null && b != null && String(a) === String(b);
const clean = (value) => String(value || "").trim();
const userLabel = (user) => clean(user?.name || user?.identity) || [user?.first_name, user?.last_name].map(clean).filter(Boolean).join(" ") || "Utilisateur sans nom";
const usersFrom = (payload) => Array.isArray(payload?.data) ? payload.data.map((user) => ({ id: user.user_id, label: userLabel(user) })).filter((user) => user.id != null).sort((a, b) => a.label.localeCompare(b.label, "fr")) : [];
const keyed = (rows) => (Array.isArray(rows) ? rows : []).map((row) => ({ ...row, _key: row._key || nextKey() }));
const normalize = (data) => ({ ...EMPTY, ...data, processus: { ...(data?.processus || {}) }, activites: keyed(data?.activites), indicateurs: keyed(data?.indicateurs), ressources: keyed(data?.ressources), autres: keyed(data?.autres), logigramme: keyed(data?.logigramme) });
const download = (content, type, name) => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a");
  link.href = url; link.download = name; document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
};
const csvCell = (value) => '"' + String(value ?? "").replace(/"/g, '""').replace(/\r?\n/g, " | ") + '"';

const RowDialog = ({ definition, leaders, onClose, onSave, open, row }) => {
  const [draft, setDraft] = useState(row || {});
  useEffect(() => setDraft(row || {}), [open, row]);
  const update = (field, value) => setDraft((current) => ({ ...current, [field]: value }));
  const valid = definition.columns.some(([field]) => field === "leader_smq_id" ? Boolean(draft[field]) : Boolean(clean(draft[field])));
  return <Dialog fullWidth maxWidth="md" onClose={onClose} open={open}>
    <DialogTitle>{row?._key ? "Modifier" : "Ajouter"} — {definition.title}</DialogTitle>
    <DialogContent dividers><Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
      {definition.columns.map(([field, label]) => field === "leader_smq_id" ? <Autocomplete
        getOptionLabel={(option) => option?.label || ""} isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
        key={field} onChange={(_, option) => setDraft((current) => ({ ...current,
          leader_smq_id: option?.id ?? null, leader_smq_name: option?.label || "" }))} options={leaders}
        renderInput={(params) => <TextField {...params} label={label} size="small" />}
        value={leaders.find((option) => sameId(option.id, draft[field])) || (draft[field] ? { id: draft[field], label: draft.leader_smq_name || "Utilisateur indisponible" } : null)}
      /> : <TextField fullWidth key={field} label={label} minRows={3} multiline onChange={(event) => update(field, event.target.value)} size="small" value={draft[field] || ""} />)}
    </Box></DialogContent>
    <DialogActions><Button onClick={onClose}>Annuler</Button><Button disabled={!valid} onClick={() => onSave(draft)} variant="contained">Valider</Button></DialogActions>
  </Dialog>;
};

const CipTable = ({ definition, leaders, onChange, rows }) => {
  const [dialog, setDialog] = useState({ index: -1, open: false, row: null });
  const close = () => setDialog({ index: -1, open: false, row: null });
  const save = (draft) => {
    const next = [...rows]; const value = { ...draft, _key: draft._key || nextKey() };
    if (dialog.index < 0) next.push(value); else next[dialog.index] = value;
    onChange(next); close();
  };
  const remove = (index) => {
    if (window.confirm("Supprimer cette ligne de la fiche processus ?")) onChange(rows.filter((_, current) => current !== index));
  };
  return <Paper elevation={0} sx={{ border: "1px solid " + OUTIL_COLORS.border, borderRadius: 1, overflow: "hidden" }}>
    <Box sx={{ alignItems: "center", backgroundColor: OUTIL_COLORS.panelHeader, display: "flex", justifyContent: "space-between", px: 1.25, py: .5 }}>
      <Typography color={OUTIL_COLORS.navy} fontSize={10.5} fontWeight={700}>{definition.title}</Typography>
      <Button onClick={() => setDialog({ index: -1, open: true, row: {} })} size="small" startIcon={<AddIcon />}>Ajouter</Button>
    </Box>
    <TableContainer><Table size="small" sx={{ minWidth: Math.max(420, definition.columns.length * 170 + 100) }}>
      <TableHead><TableRow><TableCell sx={tableHeaderSx}>N°</TableCell>
        {definition.columns.map(([, label]) => <TableCell key={label} sx={tableHeaderSx}>{label}</TableCell>)}
        <TableCell sx={tableHeaderSx}>Actions</TableCell></TableRow></TableHead>
      <TableBody>
        {!rows.length && <TableRow><TableCell align="center" colSpan={definition.columns.length + 2} sx={{ py: 2 }}>Aucune donnée. Utilisez « Ajouter ».</TableCell></TableRow>}
        {rows.map((row, index) => <TableRow key={row._key}><TableCell>{index + 1}</TableCell>
          {definition.columns.map(([field]) => <TableCell key={field} sx={{ whiteSpace: "pre-wrap" }}>{field === "leader_smq_id" ? row.leader_smq_name || leaders.find((u) => sameId(u.id, row[field]))?.label || "—" : clean(row[field]) || "—"}</TableCell>)}
          <TableCell sx={{ whiteSpace: "nowrap" }}>
            <Tooltip title="Modifier"><IconButton onClick={() => setDialog({ index, open: true, row: { ...row } })} size="small"><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Supprimer"><IconButton color="error" onClick={() => remove(index)} size="small"><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
          </TableCell></TableRow>)}
      </TableBody>
    </Table></TableContainer>
    <RowDialog definition={definition} leaders={leaders} onClose={close} onSave={save} open={dialog.open} row={dialog.row} />
  </Paper>;
};

const Identity = ({ fiche, isAdmin, leaders, onCollection, onProcess, pilots }) => {
  const process = fiche.processus;
  const pilot = pilots.find((option) => sameId(option.id, process.pilote_id)) || (process.pilote_id ? { id: process.pilote_id, label: process.pilote_name || "Pilote indisponible" } : null);
  return <Box sx={{ p: 1.5 }}>
    <Box sx={{ display: "grid", gap: 1.25, gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" } }}>
      {[["Instance", process.instance], ["Référentiel", process.referentiel_nom], ["Catégorie", process.categorie_name], ["Direction", [process.direction_code, process.direction_name].filter(Boolean).join(" — ")]].map(([label, value]) =>
        <TextField disabled key={label} label={label} size="small" sx={fieldSx} value={value || ""} />)}
      <Autocomplete disabled={!isAdmin} getOptionLabel={(option) => option?.label || ""} isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
        onChange={(_, option) => {
          onProcess("pilote_id", option?.id ?? null);
          onProcess("pilote_name", option?.label || "");
        }} options={pilots}
        renderInput={(params) => <TextField {...params} label="Pilote" size="small" sx={fieldSx} />} value={pilot} />
    </Box>
    <TextField disabled={!isAdmin} fullWidth label="Mission" minRows={3} multiline onChange={(event) => onProcess("mission", event.target.value)}
      size="small" sx={{ ...fieldSx, mt: 1.25 }} value={process.mission || ""} />
    {!isAdmin && <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} mt={.5}>Le pilote et la mission sont modifiables uniquement par un administrateur.</Typography>}
    <Box sx={{ borderTop: "1px solid " + OUTIL_COLORS.border, display: "grid", gap: 1.25, gridTemplateColumns: { xs: "1fr", xl: "1fr 1fr" }, mt: 1.5, pt: 1.5 }}>
      {Object.entries(TABLES).map(([type, definition]) => <CipTable definition={definition} key={type} leaders={leaders}
        onChange={(rows) => onCollection(type, rows)} rows={fiche[type]} />)}
    </Box>
  </Box>;
};

const retarget = (before, after) => {
  const targetKeys = new Map(before.map((row, index) => [index + 1, row._key]));
  const nextOrders = new Map(after.map((row, index) => [row._key, index + 1]));
  return after.map((row) => {
    if (row.symbole !== "condition" || !row.no_target) return { ...row, no_target: null };
    const key = targetKeys.get(Number(row.no_target));
    return { ...row, no_target: key && nextOrders.has(key) ? nextOrders.get(key) : null };
  });
};
const Diagram = ({ rows }) => <Paper elevation={0} sx={{ backgroundColor: "#f7fafd", border: "1px solid " + OUTIL_COLORS.border, mt: 1.5, overflowX: "auto", p: 1.5 }}>
  {!rows.length ? <Typography align="center" py={5}>Ajoutez une étape pour générer le logigramme.</Typography> :
    <Box sx={{ minWidth: 650 }}>{rows.map((row, index) => {
      const condition = row.symbole === "condition";
      const color = SYMBOLS.find((item) => item.value === row.symbole)?.color || OUTIL_COLORS.navy;
      return <Box alignItems="center" display="flex" flexDirection="column" key={row._key}>
        {row.responsable && <Typography color="#7890a8" fontSize={9} fontStyle="italic">{row.responsable}</Typography>}
        <Box sx={{ alignItems: "center", backgroundColor: color, borderRadius: row.symbole === "start_end" ? 99 : 1, color: "#fff", display: "flex",
          height: condition ? 72 : 45, justifyContent: "center", px: 2, transform: condition ? "rotate(45deg)" : "none", width: condition ? 72 : 210 }}>
          <Typography fontSize={9.5} fontWeight={700} sx={{ textAlign: "center", transform: condition ? "rotate(-45deg)" : "none" }}>
            {row.activite || "Étape " + (index + 1)}
          </Typography>
        </Box>
        {condition && row.no_target && <Typography color="error" fontSize={9}>Non → N° {row.no_target}</Typography>}
        {row.documents && <Typography fontSize={9}>{row.documents}</Typography>}
        {index < rows.length - 1 && <Typography fontSize={22}>↓</Typography>}
      </Box>;
    })}</Box>}
</Paper>;
const svgContent = (rows) => {
  const escape = (value) => String(value || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const height = Math.max(220, rows.length * 110 + 50);
  const body = rows.map((row, index) => {
    const y = 30 + index * 110; const color = SYMBOLS.find((item) => item.value === row.symbole)?.color || "#173d61";
    const shape = row.symbole === "condition"
      ? '<polygon points="360,' + y + ' 425,' + (y + 38) + ' 360,' + (y + 76) + ' 295,' + (y + 38) + '" fill="' + color + '"/>'
      : '<rect x="250" y="' + y + '" width="220" height="58" rx="' + (row.symbole === "start_end" ? 29 : 6) + '" fill="' + color + '"/>';
    const arrow = index < rows.length - 1 ? '<line x1="360" y1="' + (y + 78) + '" x2="360" y2="' + (y + 102) + '" stroke="#536b80" stroke-width="2"/>' : "";
    return shape + '<text x="360" y="' + (y + 35) + '" text-anchor="middle" fill="#fff" font-family="Arial" font-size="12">' +
      escape(row.activite || "Étape " + (index + 1)) + "</text>" + arrow;
  }).join("");
  return '<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" width="720" height="' + height +
    '" viewBox="0 0 720 ' + height + '"><rect width="100%" height="100%" fill="#eef5fb"/>' + body + "</svg>";
};
const Logigramme = ({ onChange, rows }) => {
  const update = (key, field, value) => onChange(rows.map((row) => row._key === key
    ? { ...row, [field]: value, ...(field === "symbole" && value !== "condition" ? { no_target: null } : {}) } : row));
  const move = (index, offset) => {
    const target = index + offset; if (target < 0 || target >= rows.length) return;
    const next = [...rows]; [next[index], next[target]] = [next[target], next[index]]; onChange(retarget(rows, next));
  };
  const remove = (key) => {
    if (window.confirm("Supprimer cette étape du logigramme ?")) onChange(retarget(rows, rows.filter((row) => row._key !== key)));
  };
  return <Box sx={{ p: 1.5 }}>
    <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-between", mb: 1 }}>
      <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>Tableau de saisie du logigramme</Typography>
      <Box display="flex" gap={1}>
        <Button onClick={() => onChange([...rows, { _key: nextKey(), responsable: "", activite: "", symbole: "step", documents: "", no_target: null }])}
          size="small" startIcon={<AddIcon />} variant="contained">Ajouter une étape</Button>
        <Button disabled={!rows.length} onClick={() => download(svgContent(rows), "image/svg+xml;charset=utf-8", "logigramme-processus.svg")}
          size="small" startIcon={<FileDownloadOutlinedIcon />}>Exporter SVG</Button>
      </Box>
    </Box>
    <TableContainer sx={{ border: "1px solid " + OUTIL_COLORS.border }}><Table size="small" sx={{ minWidth: 1320 }}>
      <TableHead><TableRow>{["N°", "Responsable(s)", "Activité", "Symbole", "Documents", "Si Non → N°", "Actions"].map((label) =>
        <TableCell key={label} sx={tableHeaderSx}>{label}</TableCell>)}</TableRow></TableHead>
      <TableBody>{rows.map((row, index) => <TableRow key={row._key}>
        <TableCell>{index + 1}</TableCell>
        {["responsable", "activite"].map((field) => <TableCell key={field} sx={{ minWidth: 220 }}><TextField fullWidth
          onChange={(event) => update(row._key, field, event.target.value)} size="small" value={row[field] || ""} /></TableCell>)}
        <TableCell sx={{ minWidth: 190 }}><Select fullWidth onChange={(event) => update(row._key, "symbole", event.target.value)}
          size="small" value={row.symbole || "step"}>{SYMBOLS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</Select></TableCell>
        <TableCell sx={{ minWidth: 210 }}><TextField fullWidth onChange={(event) => update(row._key, "documents", event.target.value)}
          size="small" value={row.documents || ""} /></TableCell>
        <TableCell><TextField disabled={row.symbole !== "condition"} inputProps={{ max: rows.length, min: 1 }}
          onChange={(event) => update(row._key, "no_target", event.target.value ? Number(event.target.value) : null)}
          size="small" type="number" value={row.no_target || ""} /></TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <IconButton disabled={index === 0} onClick={() => move(index, -1)} size="small"><ArrowUpwardIcon fontSize="small" /></IconButton>
          <IconButton disabled={index === rows.length - 1} onClick={() => move(index, 1)} size="small"><ArrowDownwardIcon fontSize="small" /></IconButton>
          <IconButton color="error" onClick={() => remove(row._key)} size="small"><DeleteOutlineIcon fontSize="small" /></IconButton>
        </TableCell>
      </TableRow>)}</TableBody>
    </Table></TableContainer>
    <Diagram rows={rows} />
  </Box>;
};

const csvContent = (fiche, dirty) => {
  const lines = [["Fiche processus", dirty ? "Modifications non enregistrées" : "Données enregistrées"]];
  const add = (title, headers, rows) => { lines.push([], [title], headers); rows.forEach((row) => lines.push(row)); };
  add("Identité", ["Champ", "Valeur"], [["Instance", fiche.processus.instance], ["Référentiel", fiche.processus.referentiel_nom],
    ["Catégorie", fiche.processus.categorie_name], ["Direction", fiche.processus.direction_name],
    ["Pilote", fiche.processus.pilote_name], ["Mission", fiche.processus.mission]]);
  Object.entries(TABLES).forEach(([key, definition]) => add(definition.title,
    ["Ordre", ...definition.columns.map(([, label]) => label)],
    fiche[key].map((row, index) => [index + 1, ...definition.columns.map(([field]) =>
      field === "leader_smq_id" ? row.leader_smq_name || row[field] : row[field])])));
  add("Logigramme", ["Ordre", "Responsable", "Activité", "Symbole", "Documents", "Si Non"],
    fiche.logigramme.map((row, index) => [index + 1, row.responsable, row.activite, row.symbole, row.documents, row.no_target]));
  return "\uFEFF" + lines.map((row) => row.map(csvCell).join(";")).join("\r\n");
};

const FicheProcessus = ({ onDirtyChange, onDocumentEntryRefresh, outilEntree, processus }) => {
  const { canTreat, isAdmin } = useSmqPermissions();
  const [view, setView] = useState("identity");
  const [fiche, setFiche] = useState(EMPTY);
  const [leaders, setLeaders] = useState([]);
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState({ severity: "success", text: "" });
  const requestRef = useRef(0);
  const markDirty = useCallback(() => { setDirty(true); onDirtyChange?.(true); }, [onDirtyChange]);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    setLoading(true); setError(""); setMessage({ severity: "success", text: "" });
    try {
      const results = await Promise.allSettled([
        OutilService.getFicheProcessus(processus.processus_id),
        UserService.getAllUserActive(),
        UserService.getAllUserByDirection(processus.direction_id),
      ]);
      if (requestId !== requestRef.current) return;
      if (results[0].status !== "fulfilled" || !results[0].value?.data) throw new Error();
      setFiche(normalize(results[0].value.data));
      setLeaders(results[1].status === "fulfilled" ? usersFrom(results[1].value) : []);
      setPilots(results[2].status === "fulfilled" ? usersFrom(results[2].value) : []);
      setDirty(false); onDirtyChange?.(false);
    } catch (loadError) {
      if (process.env.NODE_ENV === "development") console.error("[FicheProcessus load]", loadError);
      if (requestId === requestRef.current) setError(OUTIL_ERROR_MESSAGES.fetch);
    } finally { if (requestId === requestRef.current) setLoading(false); }
  }, [onDirtyChange, processus.direction_id, processus.processus_id]);

  useEffect(() => {
    load();
    return () => { requestRef.current += 1; onDirtyChange?.(false); };
  }, [load, onDirtyChange]);
  useEffect(() => {
    const warn = (event) => { if (dirty) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const onCollection = (field, rows) => { setFiche((current) => ({ ...current, [field]: rows })); markDirty(); };
  const onProcess = (field, value) => { setFiche((current) => ({ ...current, processus: { ...current.processus, [field]: value } })); markDirty(); };
  const cleanRows = (rows, fields) => rows.map((row, index) => {
    const result = { ordre: index + 1 }; fields.forEach((field) => { result[field] = row[field] ?? null; }); return result;
  });
  const makePayload = () => ({
    processus: { pilote_id: fiche.processus.pilote_id || null, mission: fiche.processus.mission || null },
    activites: cleanRows(fiche.activites, ["entree", "activite", "sortie"]),
    indicateurs: cleanRows(fiche.indicateurs, ["indicateur", "valeur"]),
    ressources: cleanRows(fiche.ressources, ["ressources_humaines", "ressources_materielles"]),
    autres: cleanRows(fiche.autres, ["documents", "risques", "opportunites", "leader_smq_id"]),
    logigramme: cleanRows(fiche.logigramme, ["responsable", "activite", "symbole", "documents", "no_target"]),
  });
  const save = async () => {
    if (!canTreat || !dirty || saving) return;
    setSaving(true); setMessage({ severity: "success", text: "" });
    try {
      const response = await OutilService.saveFicheProcessus(processus.processus_id, makePayload());
      const next = normalize(response?.data);
      setFiche(next); setDirty(false); onDirtyChange?.(false);
      if (!sameId(outilEntree?.outil_entree_id, next.outil_entree_id)) {
        onDocumentEntryRefresh?.(next.outil_entree_id);
      }
      setMessage({ severity: "success", text: "Fiche processus enregistrée." });
    } catch (saveError) {
      if (process.env.NODE_ENV === "development") console.error("[FicheProcessus save]", saveError);
      setMessage({ severity: "error", text: OUTIL_ERROR_MESSAGES.save });
    } finally { setSaving(false); }
  };

  if (loading) return <Paper elevation={0} sx={{ ...paperSx, p: 2 }}><Skeleton height={50} /><Skeleton height={220} /><Skeleton height={180} /></Paper>;
  if (error) return <Alert action={<Button color="inherit" onClick={load}>Réessayer</Button>} severity="error">{error}</Alert>;

  return <Paper aria-label="Fiche processus" elevation={0} role="region" sx={{ ...paperSx, overflow: "hidden" }}>
    <ToolSectionHeader icon={<DescriptionOutlinedIcon sx={{ fontSize: 15 }} />} showAdd={false} title="Outil 3 — Fiche processus / Logigramme" />
    <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-between", px: 1.5, pt: 1.25 }}>
      <Tabs onChange={(_, value) => setView(value)} value={view}>
        <Tab icon={<DescriptionOutlinedIcon />} iconPosition="start" label="Carte d'identité (CIP)" value="identity" />
        <Tab icon={<AccountTreeOutlinedIcon />} iconPosition="start" label="Logigramme" value="flowchart" />
      </Tabs>
      <Box display="flex" gap={1}>
        <Button onClick={() => download(csvContent(fiche, dirty), "text/csv;charset=utf-8",
          "fiche-processus-" + (fiche.processus.instance || "outil-3") + ".csv")}
          size="small" startIcon={<FileDownloadOutlinedIcon />}>Exporter CSV</Button>
        {!canTreat && view === "flowchart" && <Button disabled={!fiche.logigramme.length}
          onClick={() => download(svgContent(fiche.logigramme), "image/svg+xml;charset=utf-8", "logigramme-processus.svg")}
          size="small" startIcon={<FileDownloadOutlinedIcon />}>Exporter SVG</Button>}
        {canTreat && <Button color="success" disabled={!dirty || saving} onClick={save} size="small"
          startIcon={saving ? <CircularProgress color="inherit" size={16} /> : <SaveOutlinedIcon />} variant="contained">Enregistrer la fiche</Button>}
      </Box>
    </Box>
    {message.text && <Alert severity={message.severity} sx={{ mx: 1.5, mt: 1 }}>{message.text}</Alert>}
    {!canTreat && <Alert severity="info" sx={{ mx: 1.5, mt: 1 }}>Consultation seule : le droit Traitement est nécessaire pour modifier cette fiche.</Alert>}
    <Box component="fieldset" disabled={!canTreat} sx={{ border: 0, m: 0, minWidth: 0, p: 0 }}>
      {view === "identity"
        ? <Identity fiche={fiche} isAdmin={isAdmin} leaders={leaders} onCollection={onCollection} onProcess={onProcess} pilots={pilots} />
        : <Logigramme onChange={(rows) => onCollection("logigramme", rows)} rows={fiche.logigramme} />}
    </Box>
  </Paper>;
};

export default FicheProcessus;
